/**
 * @fileoverview Feature-detected BroadcastChannel factory.
 */

/**
 * The subset of the BroadcastChannel interface used across the app.
 */
export type SafeBroadcastChannel = Pick<
  BroadcastChannel,
  "postMessage" | "close" | "addEventListener" | "removeEventListener"
>;

const NO_OP_CHANNEL: SafeBroadcastChannel = {
  postMessage: () => {},
  close: () => {},
  addEventListener: () => {},
  removeEventListener: () => {},
};

/**
 * Returns a BroadcastChannel where the API is available, and an inert no-op with the
 * same surface where it is not (WebKit before Safari 15.4 does not implement it), so
 * callers use a single interface and cross-tab messaging degrades to a no-op instead
 * of throwing a ReferenceError.
 *
 * @param {string} name The channel name.
 * @returns {SafeBroadcastChannel} A real or no-op channel.
 */
export const createBroadcastChannel = (name: string): SafeBroadcastChannel =>
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel(name)
    : NO_OP_CHANNEL;
